import ProductList from './ProductList';
import { MailIcon } from '../assets/svgImages';
import { useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from '../context/userContext';
import { useAuth } from 'react-oidc-context';
import instance from '../config/axios';
import Loading from '../components/Loading';
import { BrandingLoader } from '../components/BrandingLoader';
import showToast from '../components/ToastComponent';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import CreditBalance from '../components/CreditBalance';
import BookConsultationButton from '../components/BookConsultationButton';
import { nativeService } from '../helpers/nativeService';
import {
  NativeResponseType,
  NativeResponseSource,
} from '../interface/native.interface';

const PatientHome = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [erxLoading, setErxLoading] = useState(false);
  const [credits, setStoreCredits] = useState<number>(0);
  const [username, setUsername] = useState('');

  const {
    userDetail,
    setLoadingEnable,
    setHideDropdown,
    setIsMobileDropdownOpen,
    isMobile,
    nativeView,
  } = useContext(UserContext);
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.access_token) return;
    nativeService(
      JSON.stringify({
        source: NativeResponseSource.LOGIN_STATUS,
        payload: user,
        type: NativeResponseType.OIDC_USER,
        status: true,
        isMobile,
      }),
    );
    localStorage.removeItem('selectedBrand');
    localStorage.removeItem('selectedForm');
    localStorage.removeItem('selectedArea');
  }, [user?.access_token]);

  const getStoreCredits = async () => {
    if (!user?.access_token) {
      console.error('No access token found');
      return false;
    }

    try {
      const response = await instance({
        url: '/api/v1/patient/storecredits',
        headers: {
          Authorization: `Bearer ${user.access_token}`,
        },
        method: 'GET',
      });

      if (!response || !response.data) {
        console.log('Invalid response from the store credits API');
        return false;
      }

      const storeCredits = response.data?.balance ?? 0;

      setStoreCredits(storeCredits);

      return true;
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      showToast({
        message: 'Failed to get store credits',
        type: 'error',
      });
      return false;
    }
  };

  const { isFetching: isCreditsLoading } = useQuery({
    queryKey: ['store-credits'],
    queryFn: () => getStoreCredits(),
  });

  const callPatientMedicationeRx = useCallback(async () => {
    try {
      setIsFetching(true);
      const token = user?.access_token;
      await instance.get('api/v1/erx/patient/medication', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.error('Error fetching patient medication:', err);
    } finally {
      setIsFetching(false);
    }
  }, [user?.access_token]);

  const callSyncErx = useCallback(async () => {
    try {
      setErxLoading(true);
      const syncRes = await instance.get('api/v1/sync/eRx', {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });

      if (syncRes.status === 201) {
        await callStatusErx();
      } else {
        console.error('Failed to Call Sync eRx');
        setErxLoading(false);
      }
    } catch (err) {
      console.log(err);
      setErxLoading(false);
    }
  }, [user?.access_token]);

  const callStatusErx = useCallback(async () => {
    try {
      const statuseRx = await instance.get('api/v1/sync/eRx/status', {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });

      if (statuseRx.data.status === 'IN PROGRESS') {
        setTimeout(async () => {
          await callStatusErx();
        }, 3000);
      } else {
        setErxLoading(false);
      }
    } catch (err) {
      console.error(err);
      setErxLoading(false);
    }
  }, [user?.access_token]);

  useEffect(() => {
    if (user?.access_token) {
      callSyncErx();
      callPatientMedicationeRx();
    }
  }, [user?.access_token, callSyncErx, callPatientMedicationeRx]);

  useEffect(() => {
    if (!erxLoading && !isFetching && !isCreditsLoading) {
      setLoadingEnable(false);
    } else {
      setLoadingEnable(true);
    }
  }, [erxLoading, isFetching, isCreditsLoading, setLoadingEnable]);

  useEffect(() => {
    if (!userDetail) return;
    const name = (
      userDetail.preferred_name?.trim() ||
      userDetail.first_name ||
      ''
    ).trim();
    if (name) {
      setUsername(`${name}!`);
    }
  }, [userDetail]);

  useEffect(() => {
    setHideDropdown(true);
    setIsMobileDropdownOpen(false);
  }, []);

  if (erxLoading) {
    return (
      <BrandingLoader loaderText="We are setting up your prescriptions. This might take few moments..." />
    );
  }

  if (isFetching || isCreditsLoading) {
    return <Loading />;
  }

  return (
    <div>
      <div className="flex md:justify-between flex-wrap mb-5 lg:mb-0">
        <div className="welcome-note flex items-start flex-col">
          <h1 className="text-xl lg:text-4xl font-bold">
            <span className="text-primary-blue-500">welcome home, &nbsp;</span>
            <span className="text-secondary-pink-500 break-normal mr-6">
              {username}
            </span>
          </h1>
        </div>
        <div
          className={`flex flex-col-reverse md:flex-row-reverse flex-wrap items-center gap-3 my-4 3xl:my-8 mx-auto lg:mx-0`}
        >
          {credits !== 0 && (
            <CreditBalance credits={credits} isCreditsLoading={false} />
          )}
          {userDetail?.internal && <BookConsultationButton />}
          {nativeView ? (
            <div
              className={`bg-primary-blue-500 text-white text-base font-semibold pl-4 pr-2 
                  rounded-l-full py-3 fixed right-0 top-28 3xl:top-36 flex items-center 2.5xl:mt-5 z-10 mt-3 ${localStorage.getItem('impersonateUser') ? 'mt-10' : '-mt-6'}`}
              onClick={() => window.location.replace('/contact-us')}
            >
              <MailIcon className="h-6 w-6 fill-white" />
            </div>
          ) : (
            <a
              href="https://monivearoad.com.au/contact/"
              className="bg-primary-blue-500 text-white text-base font-semibold pl-4 pr-2 
                  rounded-l-full py-3 fixed right-0 top-28 3xl:top-36 flex items-center -mt-6 2.5xl:mt-5 z-10"
            >
              <MailIcon className="h-6 w-6 fill-white" />
              <span className="ml-2 hidden md:block">Contact us</span>
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div
          className="col-span-1 xl:col-span-3 px-6 -mx-6 bg-secondary-cornflowerblue-50 pb-12 md:px-0 md:mx-0 
        md:bg-transparent md:pb-0 md:text-left"
        >
          <ProductList home={true} />
        </div>
      </div>
    </div>
  );
};

export default PatientHome;
